/**
 * Định dạng theo docs/08 §8.8:
 * - Ngày giờ `dd/MM/yyyy HH:mm` theo múi giờ người dùng
 * - Thời gian tương đối cho nhật ký, kèm tooltip giờ tuyệt đối
 * - Số phân tách hàng nghìn theo quy ước của ngôn ngữ đang chọn
 *
 * ── Vì sao có tham số `locale` (OQ-290) ────────────────────────────────────
 *
 * Ba hàm này từng cứng `'vi-VN'` và trả thẳng chuỗi `'vừa xong'`. Chúng nằm
 * ngoài `t()` nên không ai thấy: người dùng chuyển sang English, mọi nhãn đổi
 * đúng, riêng cột thời gian vẫn *"12 giờ trước"* và số vẫn `1.234` thay vì
 * `1,234`.
 *
 * > Chuỗi ngôn ngữ **không chỉ nằm trong từ điển**. Nó còn nằm trong tham số
 * > của `Intl` — nơi không có khoá i18n nào để đếm, nên mọi phép kiểm "đã dịch
 * > đủ chưa" đều bỏ qua.
 *
 * `en-GB` chứ không phải `en-US`: docs/08 §8.8 chốt thứ tự `dd/MM/yyyy`, và
 * `en-US` sẽ lật thành `MM/dd/yyyy` — ngày 03/04 đọc ra hai nghĩa khác nhau.
 */

import { useCallback, useMemo } from 'react';
import { type Locale, useLocale } from './i18n';

const INTL_LOCALE: Record<Locale, string> = { vi: 'vi-VN', en: 'en-GB' };

export function formatDateTime(
  value: string | Date | null | undefined,
  timeZone?: string,
  locale: Locale = 'vi',
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/**
 * Ngưỡng "vừa xong" là chuỗi duy nhất `Intl.RelativeTimeFormat` không lo được:
 * nó biết nói "12 giờ trước" bằng mọi thứ tiếng, nhưng "0 giây trước" thì đúng
 * chữ mà sai ý.
 */
const VUA_XONG: Record<Locale, string> = { vi: 'vừa xong', en: 'just now' };

export function formatRelative(
  value: string | Date | null | undefined,
  locale: Locale = 'vi',
): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(deltaSeconds);
  if (abs < 45) return VUA_XONG[locale];

  const formatter = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' });
  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds) return formatter.format(Math.round(deltaSeconds / seconds), unit);
  }
  return formatter.format(deltaSeconds, 'second');
}

export function formatNumber(value: number, locale: Locale = 'vi'): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale]).format(value);
}

/**
 * Ba hàm trên đã gắn sẵn ngôn ngữ đang chọn.
 *
 * Giữ đúng TÊN GỌI cũ để nơi dùng không phải sửa từng lời gọi — chỉ đổi chỗ
 * lấy chúng về. 82 lời gọi rải trong 17 tệp: đổi chữ ký từng chỗ là 82 cơ hội
 * bỏ sót, mà một chỗ sót thì không có gì báo.
 *
 * Hook chứ không phải biến module đọc `localStorage`: React phải biết giá trị
 * đã đổi để vẽ lại. Biến module thì đổi ngôn ngữ xong, những ô không đọc `t()`
 * sẽ giữ định dạng cũ tới lần vẽ sau — đúng loại lỗi chỉ hiện ra ở một vài ô.
 */
export function useFormat(): {
  formatDateTime: (value: string | Date | null | undefined, timeZone?: string) => string;
  formatRelative: (value: string | Date | null | undefined) => string;
  formatNumber: (value: number) => string;
} {
  const locale = useLocale();
  const dateTime = useCallback(
    (value: string | Date | null | undefined, timeZone?: string) =>
      formatDateTime(value, timeZone, locale),
    [locale],
  );
  const relative = useCallback(
    (value: string | Date | null | undefined) => formatRelative(value, locale),
    [locale],
  );
  const number = useCallback((value: number) => formatNumber(value, locale), [locale]);
  return useMemo(
    () => ({ formatDateTime: dateTime, formatRelative: relative, formatNumber: number }),
    [dateTime, relative, number],
  );
}

/** Chữ tắt hai ký tự cho avatar, giống prototype. */
export function initialsOf(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/** Bốn tông màu avatar có sẵn trong app.css. */
export function avatarTone(seed: string): string {
  const tones = ['tone-a', 'tone-b', 'tone-c', 'tone-d'];
  let sum = 0;
  for (const ch of seed) sum += ch.charCodeAt(0);
  return tones[sum % tones.length]!;
}

/** Mã chữ 3 ký tự cho huy hiệu vai trò (ADM, MOD…). */
export function roleBadge(slug: string): string {
  const map: Record<string, string> = {
    admin: 'ADM',
    moderator: 'MOD',
    member: 'MEM',
    viewer: 'VIE',
    owner: 'OWN',
    dns_editor: 'DNS',
    zone_manager: 'ZON',
  };
  return (
    map[slug] ??
    (slug
      .replace(/[^a-z0-9]/gi, '')
      .slice(0, 3)
      .toUpperCase() ||
      '—')
  );
}

/* ── Địa chỉ IP và vị trí phiên đăng nhập (OQ-226) ─────────────────────
 *
 * Bảng "Phiên & thiết bị" hiện thẳng giá trị thô: `::1` ở cột IP và `—` ở cột
 * Vị trí. Cả hai đều **đúng dữ liệu** nhưng không nói được điều người đọc cần:
 *
 *   - `::1` là IPv6 loopback — người dùng đọc thành "IP lạ", trong khi nó chỉ
 *     có nghĩa là "chính máy này".
 *   - `location` KHÔNG AI GHI vào (`INSERT INTO sessions` không có cột ấy), nên
 *     nó luôn `null`. Hiện `—` làm người đọc tưởng tra được mà không tra ra;
 *     thật ra hệ thống chưa từng đi tra.
 *
 * Suy vị trí từ chính IP thay vì gọi dịch vụ GeoIP ngoài: một lời gọi mạng ra
 * ngoài cho mỗi hàng trong bảng là cái giá không tương xứng, và nó gửi IP của
 * người dùng cho bên thứ ba mà không ai hỏi họ.
 */

/** Bỏ tiền tố IPv4-mapped (`::ffff:192.168.1.5` → `192.168.1.5`). */
function goVoIpv6(ip: string): string {
  return ip.replace(/^::ffff:/i, '');
}

export type LoaiMang = 'may-nay' | 'noi-bo' | 'cong-cong';

export function loaiMangCuaIp(ip: string | null | undefined): LoaiMang | null {
  if (!ip) return null;
  const s = goVoIpv6(ip.trim().toLowerCase());

  if (s === '::1' || s === '127.0.0.1' || s.startsWith('127.')) return 'may-nay';

  // RFC1918 + link-local IPv4
  if (/^10\./.test(s)) return 'noi-bo';
  if (/^192\.168\./.test(s)) return 'noi-bo';
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(s)) return 'noi-bo';
  if (/^169\.254\./.test(s)) return 'noi-bo';
  // IPv6 unique-local (fc00::/7) và link-local (fe80::/10)
  if (/^f[cd]/.test(s) || /^fe8/.test(s)) return 'noi-bo';

  return 'cong-cong';
}

/**
 * Địa chỉ IP để HIỆN cho người đọc.
 *
 * Giữ nguyên IP thật ở mọi trường hợp — đây là nhật ký bảo mật, che số đi thì
 * mất công dụng. Chỉ đổi cách trình bày cho hai giá trị mà người đọc không có
 * cách nào tự hiểu.
 */
export function hienIp(ip: string | null | undefined, nhan: Record<LoaiMang, string>): string {
  if (!ip) return '—';
  const loai = loaiMangCuaIp(ip);
  const s = goVoIpv6(ip.trim());
  return loai === 'may-nay' ? `${s} · ${nhan['may-nay']}` : s;
}

/**
 * Vị trí để HIỆN — suy từ IP khi cột `location` trống.
 *
 * Nói ra thứ biết chắc (máy này / mạng nội bộ) và nói thẳng khi không biết,
 * thay vì một dấu gạch ngang mà người đọc phải tự đoán nghĩa.
 */
export function hienViTri(
  location: string | null | undefined,
  ip: string | null | undefined,
  nhan: Record<LoaiMang, string> & { khongRo: string },
): string {
  if (location && location.trim()) return location;
  const loai = loaiMangCuaIp(ip);
  if (loai === 'may-nay') return nhan['may-nay'];
  if (loai === 'noi-bo') return nhan['noi-bo'];
  return nhan.khongRo;
}
